<template>
  <div class="edit-panel">
    <!-- 可滚动内容区 -->
    <div class="edit-panel-content">
      <!-- 基础信息区 -->
      <div class="section">
        <div class="section-title">{{ langMap.skeletonGraphBasicInfo[lang] }}</div>
        <Form :model="localGraphData" :label-width="100" ref="basicForm">
          <FormItem :label="langMap.name[lang]" prop="name" :rules="[{ required: true, message: langMap.skeletonGraphNameRequired[lang], trigger: 'blur' }]">
            <Input 
              v-model="localGraphData.name" 
              :placeholder="langMap.skeletonGraphNamePlaceholder[lang]"
              @on-change="handleDataChange"
            />
          </FormItem>
          
          <FormItem :label="langMap.belongStation[lang]" prop="stationId" :rules="[{ required: true, message: langMap.skeletonGraphBelongStationRequired[lang], trigger: 'change', type: 'number' }]">
            <Select 
              v-model="localGraphData.stationId" 
              :placeholder="langMap.skeletonGraphSelectStation[lang]"
              @on-change="handleStationChange"
            >
              <Option
                v-for="station in stationList"
                :key="station.id"
                :value="station.id"
              >
                {{ station.name }}
              </Option>
            </Select>
          </FormItem>
          
          <FormItem :label="langMap.description[lang]">
            <Input 
              v-model="localGraphData.description" 
              type="textarea" 
              :rows="2"
              :placeholder="langMap.skeletonGraphDescPlaceholder[lang]"
              @on-change="handleDataChange"
            />
          </FormItem>
        </Form>
      </div>

      <!-- 节点管理区 -->
      <div class="section">
        <div class="section-title">
          {{ langMap.skeletonGraphNodeManage[lang] }}
          <div class="section-actions">
            <Button size="small" type="primary" @click="handleMapAddNode" class="compact-btn">
              <Icon type="md-pin" />
              {{ isMapAddMode ? langMap.skeletonGraphExitAdd[lang] : langMap.skeletonGraphMapAdd[lang] }}
            </Button>
            <Button size="small" @click="handleImportData" class="compact-btn">
              <Icon type="md-download" />
              {{ langMap.skeletonGraphDataImport[lang] }}
            </Button>
            <Button size="small" type="primary" @click="handleManualConnect" class="compact-btn">
              <Icon type="md-git-branch" />
              {{ isManualConnectMode ? langMap.skeletonGraphExitConnect[lang] : langMap.skeletonGraphManualConnect[lang] }}
            </Button>
            
            <!-- 智能连线设置 -->
            <div class="smart-connect-wrapper">
              <span class="setting-label">{{ langMap.skeletonGraphSmartConnect[lang] }}</span>
              <i-switch 
                v-model="localGraphData.config.autoConnect"
                @on-change="handleAutoConnectChange"
                size="small"
              >
                <span slot="open">{{ langMap.on[lang] }}</span>
                <span slot="close">{{ langMap.off[lang] }}</span>
              </i-switch>
              
              <!-- 齿轮图标 - 只在开启时显示 -->
              <Poptip
                v-if="localGraphData.config.autoConnect"
                v-model="showConnectSettings"
                trigger="click"
                placement="bottom-end"
                width="280"
                transfer
              >
                <Icon 
                  type="md-settings" 
                  class="settings-gear-icon"
                  :class="{ 'active': showConnectSettings }"
                  :title="langMap.skeletonGraphConnectSettings[lang]"
                />
                <div slot="content" class="connect-settings-dropdown">
                  <div class="settings-section">
                    <div class="section-title">{{ langMap.skeletonGraphManualConnectType[lang] }}</div>
                    <RadioGroup v-model="localGraphData.config.manualConnectMode" @on-change="handleDataChange">
                      <div class="radio-list">
                        <div class="radio-item">
                          <Radio label="direct">
                            <span>{{ langMap.skeletonGraphRangeConnect[lang] }}</span>
                          </Radio>
                          <Tooltip :content="langMap.skeletonGraphRangeConnectTip[lang]" placement="right" transfer max-width="250">
                            <Icon type="md-help-circle" class="help-icon" />
                          </Tooltip>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  <Divider style="margin: 12px 0;" />
                  
                  <div class="settings-section">
                    <div class="section-title">{{ langMap.skeletonGraphDynamicConnectType[lang] }}</div>
                    <RadioGroup v-model="localGraphData.config.connectMode" @on-change="handleConnectModeChange">
                      <div class="radio-list">
                        <div class="radio-item">
                          <Radio label="insert">
                            <span>{{ langMap.skeletonGraphInsertConnect[lang] }}</span>
                          </Radio>
                          <Tooltip :content="langMap.skeletonGraphInsertConnectTip[lang]" placement="right" transfer max-width="250">
                            <Icon type="md-help-circle" class="help-icon" />
                          </Tooltip>
                        </div>
                        
                        <!-- 顺序连接功能已注释
                        <div class="radio-item">
                          <Radio label="sequential">
                            <span>{{ langMap.skeletonGraphSequentialConnect[lang] }}</span>
                          </Radio>
                          <Tooltip :content="langMap.skeletonGraphSequentialConnectTip[lang]" placement="right" transfer max-width="250">
                            <Icon type="md-help-circle" class="help-icon" />
                          </Tooltip>
                        </div>
                        -->
                        
                        <div class="radio-item">
                          <Radio label="nearest">
                            <span>{{ langMap.skeletonGraphNearestConnect[lang] }}</span>
                          </Radio>
                          <Tooltip :content="langMap.skeletonGraphNearestConnectTip[lang]" placement="right" transfer max-width="250">
                            <Icon type="md-help-circle" class="help-icon" />
                          </Tooltip>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </Poptip>
            </div>
          </div>
        </div>
        
        <div class="node-list">
          <div v-if="localGraphData.nodes && localGraphData.nodes.length > 0">
            <div 
              v-for="node in localGraphData.nodes" 
              :key="node.id"
              class="node-item-compact"
            >
              <div class="node-compact-header">
                <span class="node-id">{{ langMap.skeletonGraphNode[lang] }} {{ node.id }}</span>
                <Button 
                  type="error" 
                  size="small" 
                  icon="md-trash"
                  @click="handleDeleteNode(node.id)"
                  :title="langMap.skeletonGraphDeleteNode[lang]"
                />
              </div>
              <div class="node-compact-coords">
                <div class="coord-compact-item">
                  <span class="coord-label">{{ langMap.jd[lang] }}</span>
                  <InputNumber
                    v-model="node.wgs84Lon"
                    :precision="7"
                    :step="0.0000001"
                    :max="180"
                    :min="-180"
                    size="small"
                    style="width: 110px"
                    @on-blur="handleNodeUpdate(node)"
                  />
                </div>
                <div class="coord-compact-item">
                  <span class="coord-label">{{ langMap.wd[lang] }}</span>
                  <InputNumber
                    v-model="node.wgs84Lat"
                    :precision="7"
                    :step="0.0000001"
                    :max="90"
                    :min="-90"
                    size="small"
                    style="width: 110px"
                    @on-blur="handleNodeUpdate(node)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <p>{{ langMap.skeletonGraphNoNodes[lang] }}</p>
            <p class="tip">{{ langMap.skeletonGraphNoNodesTip[lang] }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 固定在底部的操作按钮区 -->
    <div class="action-buttons">
      <Button @click="handleCancel" style="margin-right: 8px">
        {{ langMap.cancel[lang] }}
      </Button>
      <Button type="primary" @click="handleSave" :loading="saving">
        {{ isNewMode ? langMap.save[lang] : langMap.modify[lang] }}
      </Button>
    </div>
  </div>
</template>

<script>
import dataValidator from '@/utils/skeletonGraph/dataValidator'
import langMap from '@/utils/language'

export default {
  name: 'EditPanel',
  props: {
    graphData: {
      type: Object,
      required: true
    },
    stationList: {
      type: Array,
      default: () => []
    },
    isMapAddMode: {
      type: Boolean,
      default: false
    },
    isManualConnectMode: {
      type: Boolean,
      default: false
    },
    isCreatingNew: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      langMap,
      localGraphData: null,
      saving: false,
      showConnectSettings: false,  // 控制连线设置浮窗显示
      originalGraphData: null,  // 保存原始数据用于比较
      hasChanges: false,  // 标记是否有修改
      lastNodeCount: 0  // 记录上次的节点数量
    }
  },
  computed: {
    // 判断是否为新建模式
    isNewMode() {
      return this.isCreatingNew
    },
    lang() {
      return this.$store.state.app.language
    }
  },
  watch: {
    graphData: {
      handler(newVal) {
        if (newVal) {
          this.localGraphData = JSON.parse(JSON.stringify(newVal))
          // 保存原始数据的深拷贝
          this.originalGraphData = JSON.parse(JSON.stringify(newVal))
          this.hasChanges = false
          
          // 记录节点数量
          this.lastNodeCount = newVal.nodes ? newVal.nodes.length : 0
          
          // 确保config对象存在
          if (!this.localGraphData.config) {
            this.localGraphData.config = {
              autoConnect: false,
              connectMode: 'insert',
              manualConnectMode: 'direct'
            }
          }
          // 确保所有必需字段存在
          if (this.localGraphData.config.autoConnect === undefined) {
            this.localGraphData.config.autoConnect = false;
          }
          if (!this.localGraphData.config.connectMode) {
            this.localGraphData.config.connectMode = 'insert';
          }
          if (!this.localGraphData.config.manualConnectMode) {
            this.localGraphData.config.manualConnectMode = 'direct';
          }
          // 确保nodes数组存在
          if (!this.localGraphData.nodes) {
            this.localGraphData.nodes = []
          }
        }
      },
      immediate: true,
      deep: true
    },
    localGraphData: {
      handler(newVal) {
        // 检测数据是否有变化
        if (this.originalGraphData && newVal) {
          // 只比较关键字段，避免因为浮点数精度问题导致误判
          const hasNameChange = newVal.name !== this.originalGraphData.name
          const hasDescChange = newVal.description !== this.originalGraphData.description
          const hasStationChange = newVal.stationId !== this.originalGraphData.stationId
          const hasConfigChange = JSON.stringify(newVal.config) !== JSON.stringify(this.originalGraphData.config)
          
          // 比较节点数量
          const hasNodeCountChange = (newVal.nodes?.length || 0) !== (this.originalGraphData.nodes?.length || 0)
          
          // 如果节点数量增加，滚动到最后一个节点
          if (hasNodeCountChange && newVal.nodes && newVal.nodes.length > this.lastNodeCount) {
            this.$nextTick(() => {
              this.scrollToLastNode();
            });
            this.lastNodeCount = newVal.nodes.length;
          }
          
          // 比较节点内容（只比较ID和坐标，忽略浮点数精度差异）
          let hasNodeContentChange = false
          if (!hasNodeCountChange && newVal.nodes && this.originalGraphData.nodes) {
            for (let i = 0; i < newVal.nodes.length; i++) {
              const newNode = newVal.nodes[i]
              const oldNode = this.originalGraphData.nodes[i]
              
              // 比较ID
              if (newNode.id !== oldNode.id) {
                hasNodeContentChange = true
                break
              }
              
              // 比较坐标（保留6位小数比较，避免精度问题）
              const newLon = Number(newNode.wgs84Lon?.toFixed(6) || 0)
              const oldLon = Number(oldNode.wgs84Lon?.toFixed(6) || 0)
              const newLat = Number(newNode.wgs84Lat?.toFixed(6) || 0)
              const oldLat = Number(oldNode.wgs84Lat?.toFixed(6) || 0)
              
              if (newLon !== oldLon || newLat !== oldLat) {
                hasNodeContentChange = true
                break
              }
            }
          }
          
          // 比较连线
          const hasEdgeChange = JSON.stringify(newVal.edges || []) !== JSON.stringify(this.originalGraphData.edges || [])
          
          this.hasChanges = hasNameChange || hasDescChange || hasStationChange || 
                           hasConfigChange || hasNodeCountChange || hasNodeContentChange || hasEdgeChange
          this.$emit('change-status', this.hasChanges)
        }
      },
      deep: true
    }
  },
  methods: {
    handleDataChange() {
      this.$emit('update:graphData', this.localGraphData)
    },
    
    handleStationChange(stationId) {
      this.handleDataChange();
      this.$emit('station-change', stationId);
    },
    
    handleAutoConnectChange(value) {
      // 开启或关闭自动连线时，通知父组件清空连线序列
      // 但不重置用户选择的连线模式，保持用户的设置
      this.$emit('connect-mode-change');
      this.handleDataChange();
    },
    
    handleConnectModeChange() {
      // 连线模式变化时，通知父组件重置连线序列
      this.$emit('connect-mode-change');
      this.handleDataChange();
    },
    
    getModeDescription() {
      const mode = this.localGraphData.config.connectMode;
      const descriptions = {
        sequential: this.langMap.skeletonGraphSequentialConnectTip[this.lang],
        insert: this.langMap.skeletonGraphInsertConnectTip[this.lang],
        nearest: this.langMap.skeletonGraphNearestConnectTip[this.lang]
      };
      return descriptions[mode] || '';
    },
    
    handleMapAddNode() {
      this.$emit('map-add-node')
    },
    
    handleManualConnect() {
      this.$emit('manual-connect')
    },
    
    handleImportData() {
      this.$emit('import-data')
    },
    
    handleDeleteNode(nodeId) {
      this.$Modal.confirm({
        title: this.langMap.confirmDelete[this.lang],
        content: this.langMap.skeletonGraphDeleteNodeConfirm[this.lang],
        onOk: () => {
          this.$emit('delete-node', nodeId)
        }
      })
    },
    
    handleNodeUpdate(node) {
      // 验证坐标
      const result = dataValidator.validateCoordinate(node.wgs84Lon, node.wgs84Lat)
      if (!result.valid) {
        this.$Message.error(result.errors.join('; '))
        return
      }
      
      this.$emit('update-node', node)
    },
    
    handleSave() {
      // 验证表单
      this.$refs.basicForm.validate((valid) => {
        if (!valid) {
          this.$Message.error(this.langMap.skeletonGraphValidationFailed[this.lang])
          return
        }
        
        // 验证数据
        if (!this.validateData()) {
          return
        }
        
        this.$emit('save')
      })
    },
    
    handleCancel() {
      this.$emit('cancel')
    },
    
    validateData() {
      const result = dataValidator.validateGraphData(this.localGraphData)
      
      if (!result.valid) {
        this.$Notice.error({
          title: this.langMap.skeletonGraphValidationFailed[this.lang],
          desc: result.errors.join('; '),
          duration: 5
        })
        return false
      }
      
      return true
    },
    
    resetForm() {
      this.$refs.basicForm.resetFields()
    },
    
    // 滚动到最后一个节点
    scrollToLastNode() {
      const nodeList = this.$el.querySelector('.node-list');
      if (nodeList) {
        // 滚动到底部
        nodeList.scrollTop = nodeList.scrollHeight;
      }
    }
  }
}
</script>

<style scoped lang="less">
.edit-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  .edit-panel-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 8px;
    padding-bottom: 0;
    
    .section {
      flex-shrink: 0;
      margin-bottom: 20px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .section-title {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid #e8eaec;
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .section-actions {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          align-items: center;
        }
      }
    }
    
    // 节点管理区域特殊处理：允许内部滚动
    .section:nth-child(2) {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-height: 0;
      
      .node-list {
        flex: 1;
        overflow-y: auto;
        min-height: 0;
      }
    }
    
    .node-list {
      .node-item-compact {
        padding: 8px 10px;
        margin-bottom: 6px;
        border: 1px solid #e8eaec;
        border-radius: 4px;
        background: #f8f8f9;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .node-compact-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
          
          .node-id {
            font-weight: 600;
            font-size: 13px;
            color: #2d8cf0;
          }
        }
        
        .node-compact-coords {
          display: flex;
          gap: 8px;
          
          .coord-compact-item {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 4px;
            
            .coord-label {
              font-size: 12px;
              color: #666;
              white-space: nowrap;
            }
          }
        }
      }
      
      .empty-state {
        text-align: center;
        padding: 40px 20px;
        color: #808695;
        
        p {
          margin: 8px 0;
        }
        
        .tip {
          font-size: 12px;
          color: #c5c8ce;
        }
      }
    }
  }
  
  // 连线设置浮窗样式
  .connect-settings-popover {
    
    .setting-row {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .setting-label {
        font-size: 14px;
        color: #515a6e;
        width: 55px;
        margin-right: 12px;
        font-weight: 500;
        flex-shrink: 0;
      }
      
      // 确保开关有足够的宽度显示文字
      .ivu-switch {
        min-width: 60px;
        
        &.ivu-switch-checked:after {
          left: 100%;
          margin-left: -19px;
        }
      }
      
      .ivu-radio-group {
        display: flex;
        gap: 12px;
      }
    }
  }
  
  .inline-form-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  
  .action-buttons {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    padding: 10px 16px 0 16px;
    border-top: 1px solid #e8eaec;
    background: #fff;
  }
}
</style>

<style lang="less">
// 连线设置浮窗全局样式（因为使用了transfer）
.connect-settings-popover {
  .setting-row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .setting-label {
      font-size: 14px;
      color: #515a6e;
      width: 55px;
      margin-right: 12px;
      font-weight: 500;
      flex-shrink: 0;
    }
    
    .ivu-switch {
      min-width: 60px;
      
      &.ivu-switch-checked:after {
        left: 100%;
        margin-left: -19px;
      }
    }
    
    .ivu-radio-group {
      display: flex;
      gap: 12px;
    }
  }
}
</style>


<style scoped lang="less">
// 紧凑按钮样式
.compact-btn {
  padding: 2px 8px !important;
  font-size: 12px !important;
  height: 24px !important;
  line-height: 1 !important;
  
  i {
    font-size: 13px !important;
    margin-right: 2px !important;
  }
  
  span {
    vertical-align: middle;
  }
}

</style>

<style lang="less">
// 智能连线设置包装器
.smart-connect-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: #f8f8f9;
  border-radius: 3px;

  .setting-label {
    font-size: 12px;
    color: #515a6e;
    font-weight: 500;
  }

  .ivu-switch-small {
    width: 36px !important;
    height: 18px !important;
    line-height: 16px !important;

    &:after {
      width: 14px !important;
      height: 14px !important;
    }

    &.ivu-switch-checked:after {
      left: 20px !important;
    }

    .ivu-switch-inner {
      font-size: 11px !important;
      left: 16px !important;
    }
  }
  
  .ivu-switch-checked .ivu-switch-inner {
    left: 4px !important;
  }

  .settings-gear-icon {
    font-size: 14px;
    color: #808695;
    cursor: pointer;
    transition: all 0.3s;
    padding: 2px;
    border-radius: 3px;

    &:hover {
      color: #2d8cf0;
      background: #e8f4ff;
    }

    &.active {
      color: #2d8cf0;
      background: #d5e8f7;
    }
  }
}
// 连线设置下拉浮窗全局样式（因为使用了transfer）
.connect-settings-dropdown {
  padding: 10px;
  
  .settings-section {
    .section-title {
      font-size: 12px;
      font-weight: 600;
      color: #17233d;
      margin-bottom: 8px;
    }
    
    .ivu-radio-group {
      width: 100%;
    }
    
    .radio-list {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    
    .radio-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 8px;
      border-radius: 3px;
      transition: background 0.2s;
      
      &:hover {
        background: #f8f8f9;
      }
      
      .ivu-radio-wrapper {
        flex: 1;
        margin-right: 8px;
        
        span {
          font-size: 13px;
          color: #515a6e;
        }
      }
      
      .help-icon {
        font-size: 14px;
        color: #999;
        cursor: help;
        flex-shrink: 0;
        
        &:hover {
          color: #2d8cf0;
        }
      }
    }
  }
}
</style>
